import type { CodeModeCall } from '@/lib/code-sessions/code-mode-calls';
import type { SessionMessageInfo } from '@opencode/client/promise';

import type { SessionPart } from './transcript-model';

import { codeModeCalls, mergeCodeModeCalls } from '@/lib/code-sessions/code-mode-calls';
import { stringInput } from '@/lib/code-sessions/tool-steps';
import { OPENCODE_CODE_MODE_TOOL_NAME } from '@/lib/mcp-servers/tool-names';

export type CodeModeScript = { code?: string; calls: CodeModeCall[] };

/** Metadata has its own fold: a completed saved tool must not suppress richer live progress. */
export function seedCodeModeScripts(
  messages: readonly SessionMessageInfo[],
): Map<string, CodeModeScript> {
  const scripts = new Map<string, CodeModeScript>();
  for (const message of messages) {
    if (message.type !== 'assistant') {
      continue;
    }
    for (const content of message.content) {
      if (content.type === 'tool' && content.name === OPENCODE_CODE_MODE_TOOL_NAME) {
        recordCodeModeScript(scripts, `${message.id}:tool:${content.id}`, {
          ...content.state,
          name: content.name,
        });
      }
    }
  }
  return scripts;
}

export function recordCodeModeScript(
  scripts: Map<string, CodeModeScript>,
  key: string | undefined,
  data: Record<string, unknown>,
): void {
  if (key === undefined) {
    return;
  }
  let script = scripts.get(key);
  if (script === undefined) {
    if (data.name !== OPENCODE_CODE_MODE_TOOL_NAME) {
      return;
    }
    script = { calls: [] };
    scripts.set(key, script);
  }
  const code = stringInput(data.input, ['code']);
  if (code) {
    script.code = code;
  } else if (script.code === undefined && typeof data.text === 'string') {
    try {
      script.code = stringInput(JSON.parse(data.text), ['code']) || undefined;
    } catch {
      // The script's JSON input may still be streaming.
    }
  }
  // Code Mode's children name the tools this event's metadata records. Their
  // names never move back: an event that records none, such as `called`
  // before a tool lands or a success after a search-only script, keeps what
  // an earlier one set. The parent names the script, not an assumed MCP call.
  script.calls = mergeCodeModeCalls(script.calls, codeModeCalls(data.metadata));
}

/** Join by call identity, not arrival order or the generic trace step's lifecycle. */
export function showCodeModeScripts(
  messageParts: Map<string, SessionPart[]>,
  scripts: Map<string, CodeModeScript>,
): void {
  for (const [messageId, parts] of messageParts) {
    for (const part of parts) {
      if (part.type !== 'trace') {
        continue;
      }
      for (const step of part.steps) {
        if (step.callId !== undefined) {
          const script = scripts.get(`${messageId}:tool:${step.callId}`);
          if (script !== undefined) {
            step.codeMode = script;
          }
        }
      }
    }
  }
}
